import React, {useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation, Trans} from "react-i18next";
import * as ApiHelper from "../../../api/ApiHelper";
import * as ErrorCodes from "../../../api/ErrorCodes";
import ErrorResponse from "../../../models/ErrorResponse";
import {useAbortController} from "../../../hooks";
import Loader from "../../Widgets/Loader/Loader";
import {Button} from "../../Widgets/Button/Button";
import {PasswordBox} from "../../Widgets/InputBox/PasswordBox";
import {AccountSettings} from "../../Widgets/AccountSettings/AccountSettings";
import {ACCOUNT_ROUTE, SIGN_IN_ROUTE} from "../../../routes";
import {useEmailLinkLanguage} from "../useEmailLinkLanguage";

enum Field {
    Password,
    ConfirmPassword
}

type ViewState = "form" | "success" | "failed";

function ResetPassword() {
    const {t, i18n} = useTranslation();
    const history = useNavigate();
    const [controller] = useAbortController();
    const {searchParams, language} = useEmailLinkLanguage();
    const userId = searchParams.get("userId") ?? "";
    const code = searchParams.get("code") ?? "";

    const [viewState, setViewState] = useState<ViewState>(userId && code ? "form" : "failed");
    const [isFetching, setFetchingState] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [badField, setBadField] = useState<Field | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [email, setEmail] = useState<string | null>(null);

    const handleSignInClick = () => {
        history(ACCOUNT_ROUTE + SIGN_IN_ROUTE, {replace: true});
    };

    const errorHandler = (error: ErrorResponse) => {
        setFetchingState(false);
        if (error.ResultCode === ErrorCodes.BadPassword) {
            setErrorMessage(t("incorrect_password_full"));
            setBadField(Field.Password);
            return;
        }
        if (error.ResultCode === ErrorCodes.PasswordMismatch) {
            setErrorMessage(t("password_mismatch"));
            setBadField(Field.ConfirmPassword);
            return;
        }
        setViewState("failed");
    };

    const handleResetClick = () => {
        if (!userId || !code) {
            setViewState("failed");
            return;
        }
        if (password.trim() === "") {
            setBadField(Field.Password);
            setErrorMessage(t("enter_password"));
            return;
        }
        if (password !== confirmPassword) {
            setBadField(Field.ConfirmPassword);
            setErrorMessage(t("password_mismatch"));
            return;
        }
        setBadField(null);
        setErrorMessage(null);
        setFetchingState(true);
        ApiHelper.resetPasswordWithToken(
            userId,
            code,
            password,
            confirmPassword,
            language || i18n.language,
            controller,
            (resetEmail) => {
                setEmail(resetEmail);
                setFetchingState(false);
                setViewState("success");
            },
            errorHandler
        );
    };

    if (isFetching) {
        return <Loader/>;
    }

    if (viewState === "success") {
        return (
            <div className="window-modal-container">
                <div className="window-modal">
                    <div className="window-modal-title disable-select pb-4 d-flex justify-content-center">
                        {t("password_reset_title")}
                    </div>
                    <div className="window-modal-text my-2">
                        <Trans
                            i18nKey="password_reset_from_email_success"
                            values={{email: email ?? ""}}
                            components={{bold: <strong/>}}
                        />
                    </div>
                    <div className="d-flex justify-content-center">
                        <div className="window-modal-button">
                            <Button className="mt-4" text={t("sign_in")} highlighted={true} onClick={handleSignInClick}/>
                        </div>
                    </div>
                    <div className="mt-4"/>
                    <AccountSettings/>
                </div>
            </div>
        );
    }

    if (viewState === "failed") {
        return (
            <div className="window-modal-container">
                <div className="window-modal">
                    <div className="window-modal-title disable-select pb-4 d-flex justify-content-center">
                        {t("password_reset_title")}
                    </div>
                    <div className="window-modal-text my-2">{t("password_reset_from_email_failed")}</div>
                    <div className="d-flex justify-content-center">
                        <div className="window-modal-button">
                            <Button className="mt-4" text={t("sign_in")} highlighted={true} onClick={handleSignInClick}/>
                        </div>
                    </div>
                    <div className="mt-4"/>
                    <AccountSettings/>
                </div>
            </div>
        );
    }

    return (
        <div className="window-modal-container">
            <div className="window-modal">
                <div className="window-modal-title disable-select pb-4 d-flex justify-content-center">
                    {t("password_reset_title")}
                </div>
                <div className="window-modal-error d-flex justify-content-center align-items-center my-2">
                    {errorMessage ? errorMessage : ""}
                </div>
                <PasswordBox
                    className="my-2"
                    title={t("password")}
                    initialValue={password}
                    hasError={badField === Field.Password}
                    onChange={setPassword}
                />
                <PasswordBox
                    className="my-2"
                    title={t("confirm_password")}
                    initialValue={confirmPassword}
                    hasError={badField === Field.ConfirmPassword}
                    onChange={setConfirmPassword}
                />
                <div className="d-flex justify-content-center">
                    <div className="window-modal-button">
                        <Button className="mt-4" text={t("reset")} highlighted={true} onClick={handleResetClick}/>
                    </div>
                    <div className="mx-2"/>
                    <div className="window-modal-button">
                        <Button className="mt-4" text={t("cancel")} highlighted={false} onClick={handleSignInClick}/>
                    </div>
                </div>
                <div className="mt-4"/>
                <AccountSettings/>
            </div>
        </div>
    );
}

export default ResetPassword;
